import { useRef } from "react";
import type { DictationStatus } from "shared";
import { useHoldKey } from "utilz/helpers";

/** Hold either Option (Mac) / Alt (Windows) to dictate — Fn never reaches a web page. */
const PUSH_TO_TALK_KEYS = ["AltLeft", "AltRight"] as const;

/**
 * `holdDelayMs` for composers that are text editors: the mic opens only after
 * the key is held this long, so Option+← / Option+⌫ (word jumps and deletes
 * while typing) never start it.
 */
export const EDITOR_PUSH_TO_TALK_HOLD_DELAY_MS = 300;

interface UseDictationPushToTalkOptions {
  enabled: boolean;
  status: DictationStatus;
  canAccept: boolean;
  /** Which presses may start it: the whole screen, or only inside one composer. */
  isAllowedTarget: (target: EventTarget | null) => boolean;
  /** See `useHoldKey` — a composer inside a text editor needs one, so Option+← isn't a dictation. */
  holdDelayMs?: number;
  onStart: () => void;
  /** Released with words: finalise and insert, as ✓. */
  onAccept: () => void;
  /** Released before anything was said, or the key turned into a shortcut. */
  onDiscard: () => void;
}

/**
 * Push-to-talk (Wispr Flow style): hold the key to talk, release = ✓.
 * Only a dictation the key started is ended by releasing it — a mic-click
 * dictation ignores the key.
 */
export const useDictationPushToTalk = ({
  enabled,
  status,
  canAccept,
  isAllowedTarget,
  holdDelayMs,
  onStart,
  onAccept,
  onDiscard,
}: UseDictationPushToTalkOptions): void => {
  const isHoldDictationRef = useRef(false);

  useHoldKey({
    codes: PUSH_TO_TALK_KEYS,
    enabled,
    holdDelayMs,
    isAllowedTarget,
    onHoldStart: () => {
      if (status !== "idle") return;
      isHoldDictationRef.current = true;
      onStart();
    },
    onHoldEnd: () => {
      if (!isHoldDictationRef.current) return;
      isHoldDictationRef.current = false;
      // Nothing said yet (or still connecting): nothing to insert, so cancel
      // rather than leave the mic listening.
      if (canAccept) onAccept();
      else onDiscard();
    },
    onHoldCancel: () => {
      if (!isHoldDictationRef.current) return;
      isHoldDictationRef.current = false;
      onDiscard();
    },
  });
};
